// A COTA DE ARMAZENAMENTO, EM PALAVRAS.
//
// A conta é cobrada por espaço (30 GB inclusos, mais GB sob demanda) e, até aqui, NENHUMA tela
// mostrava o consumo: a pessoa descobria o limite quando o envio era recusado pelo gatilho do
// Storage. Cobrar por uma cota que o cliente não consegue acompanhar é o que gera contestação.
//
// Os números vêm prontos do servidor (`public.account_quotas`, que lê a mesma
// `storage_quota_bytes` usada para barrar o envio). AQUI NÃO SE DECIDE NADA sobre direito a
// espaço: só se traduz um par de inteiros em algo legível. Reimplementar a regra criaria uma
// segunda verdade, e seria a errada — quem manda é o banco, que é quem recusa.
//
// Vive no núcleo porque o app nativo mostra o mesmo indicador, e duas formatações divergiriam.

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

/**
 * Bytes em algo que cabe numa linha de interface.
 *
 * Uma casa decimal em GB e nenhuma em MB: "12,4 GB" ajuda a acompanhar o consumo, "847,3 MB" só
 * polui. Abaixo de 1 MB arredonda para cima até 1 MB, porque "0 MB" ao lado de um arquivo que a
 * pessoa acabou de enviar parece defeito.
 */
export function formataBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  if (bytes >= GB) return `${(bytes / GB).toFixed(1).replace('.', ',')} GB`;
  return `${Math.max(1, Math.round(bytes / MB))} MB`;
}

/**
 * O tamanho de UM arquivo, para a linha dele (Arquivos, cartão do chat).
 *
 * O `formataBytes` arredonda para cima até 1 MB, o que serve à cota e mente num arquivo pequeno:
 * um CSV de 1,5 KB aparecia como "1 MB". Aqui, abaixo de 1 MB vale KB (no mínimo 1 KB).
 */
export function tamanhoDoArquivo(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  if (bytes < MB) return `${Math.max(1, Math.round(bytes / KB))} KB`;
  if (bytes < GB) return `${(bytes / MB).toFixed(bytes < 10 * MB ? 1 : 0).replace('.', ',').replace(/,0$/, '')} MB`;
  return formataBytes(bytes);
}

export type NivelDaCota = 'tranquilo' | 'atencao' | 'cheio';

export interface EstadoDaCota {
  /** Quanto da cota já foi usado, de 0 a 1. Sem cota, é 0. */
  fracao: number;
  /** O mesmo, inteiro, para a barra e para o texto. */
  percentual: number;
  nivel: NivelDaCota;
  /** "12,4 GB de 30 GB" — o texto da linha, pronto. */
  rotulo: string;
  /** Verdadeiro a partir de 80%: é quando vale avisar, antes de o envio falhar. */
  perto: boolean;
}

/** A partir daqui o app avisa. Antes disso, o indicador informa e não interrompe. */
export const LIMIAR_DE_AVISO = 0.8;

/**
 * Traduz o par (usado, cota) no que a tela precisa.
 *
 * `cota` zero ou ausente significa "sem direito a espaço" (conta grátis), e não "espaço
 * infinito": nesse caso a fração é 1 e o nível é `cheio`, porque é exatamente isso que a pessoa
 * encontra ao tentar enviar. Cota `Infinity` (uso interno, sem paywall) devolve `tranquilo`.
 */
export function estadoDaCota(usados: number, cota: number): EstadoDaCota {
  const usado = Number.isFinite(usados) && usados > 0 ? usados : 0;

  if (cota === Infinity) {
    return { fracao: 0, percentual: 0, nivel: 'tranquilo', rotulo: formataBytes(usado), perto: false };
  }

  const limite = Number.isFinite(cota) && cota > 0 ? cota : 0;
  if (limite === 0) {
    return { fracao: 1, percentual: 100, nivel: 'cheio', rotulo: 'Sem espaço no plano grátis', perto: true };
  }

  const fracao = Math.min(1, usado / limite);
  // `floor`, e não `round`: arredondar para cima faria 99,6% virar "100%" com espaço ainda
  // sobrando, e alguém pararia de enviar achando que acabou.
  const percentual = Math.floor(fracao * 100);
  const nivel: NivelDaCota = fracao >= 1 ? 'cheio' : fracao >= LIMIAR_DE_AVISO ? 'atencao' : 'tranquilo';

  return {
    fracao,
    percentual,
    nivel,
    rotulo: `${formataBytes(usado)} de ${formataBytes(limite)}`,
    perto: fracao >= LIMIAR_DE_AVISO,
  };
}
